/**
 * Browser-side composition builders. Re-exports the pure HTML builders from
 * the engine so the React app can generate Hyperframes composition HTML on
 * the fly (for live preview & thumbnails), without going through the API.
 *
 * Everything imported here is pure (string-in, string-out) — no node deps.
 */
import { buildReelHtml, calcReelDurationSec as engineCalcReelDurationSec } from "@engine/hyperframes/reel/index";
import { HYPERFRAMES_TEMPLATES } from "@engine/hyperframes/templates/index";
import { dimensionsFor } from "@engine/hyperframes/format";
import type { QueueItem } from "@engine/lib/queue";
import type { TemplateId } from "@engine/templates/index";
import type { FormatId } from "@engine/hyperframes/format";
import type { LocaleCode } from "@engine/locales/index";

export const FPS = 30;

/**
 * Rewrite the engine's relative asset URLs into absolute dev-server URLs so
 * the composition HTML, when injected into an `<iframe srcdoc>` (about:blank
 * base URL), can still resolve `<img>`, `<audio>` and `<video>` sources.
 *
 *  - `assets/brand/<f>`         → `<origin>/brand/<f>`
 *  - `assets/sounds/<f>`        → `<origin>/sounds/<f>`
 *  - `assets/uploads/<f>`       → `<origin>/uploads/<f>`
 *  - `audio-library/<f>`        → `<origin>/audio-library/<f>`
 *  - `/static/<f>`              → `<origin>/static/<f>` (proxied to API)
 *  - `/audio-library/<f>`       → `<origin>/audio-library/<f>` (proxied to API)
 *  - `/brand/<f>`               → `<origin>/brand/<f>`    (UI-relative paths
 *  - `/uploads/<f>`             → `<origin>/uploads/<f>`   from the element
 *  - `/sounds/<f>`              → `<origin>/sounds/<f>`    library / uploads)
 *
 * The MP4 render path stages assets next to the HTML, so the relative URLs
 * stay correct there — this rewrite only applies to the live UI preview.
 */
function rewriteAssetUrlsForPreview(html: string): string {
  if (typeof window === "undefined") return html;
  const origin = window.location.origin;
  return html
    .replace(/(["'(])assets\/(brand|sounds|uploads)\//g, `$1${origin}/$2/`)
    .replace(/(["'(])audio-library\//g, `$1${origin}/audio-library/`)
    .replace(/(["'(])\/static\//g, `$1${origin}/static/`)
    .replace(/(["'(])\/audio-library\//g, `$1${origin}/audio-library/`)
    // UI-seitig referenzieren wir Brand-Assets / Uploads als absolute Pfade
    // (`/brand/logo.png`, `/uploads/foo.png`). In einem `<iframe srcDoc>` ist
    // die Base-URL `about:srcdoc`, dadurch gehen die führenden Slash-Pfade
    // ins Leere → `<img>` lädt nicht. Hier auf den dev-Server umbiegen.
    .replace(/(["'(])\/(brand|uploads|sounds)\//g, `$1${origin}/$2/`);
}

export const FORMAT_DIMENSIONS: Record<FormatId, { width: number; height: number; label: string; aspect: string }> = {
  "9:16": { ...dimensionsFor("9:16"), label: "Reels / TikTok / Shorts", aspect: "9:16" },
  "1:1": { ...dimensionsFor("1:1"), label: "Instagram Feed", aspect: "1:1" },
  "16:9": { ...dimensionsFor("16:9"), label: "YouTube / Website", aspect: "16:9" },
};

export function buildReelComposition(
  item: QueueItem,
  format: FormatId,
  locale: LocaleCode = "de",
): { html: string; durationSec: number } {
  const { width, height } = dimensionsFor(format);
  const html = rewriteAssetUrlsForPreview(
    buildReelHtml(item, { format, locale, width, height }),
  );
  const durationSec = engineCalcReelDurationSec(item);
  return { html, durationSec };
}

export function buildTemplateComposition(
  templateId: TemplateId,
  format: FormatId,
  locale: LocaleCode,
  inputProps: Record<string, unknown>,
): { html: string; durationSec: number } | null {
  const builder = (HYPERFRAMES_TEMPLATES as Record<string, { build: (p: unknown, ctx: unknown) => string; durationSeconds: (p: unknown) => number }>)[templateId as string];
  if (!builder) return null;
  const { width, height } = dimensionsFor(format);
  const durationSec = builder.durationSeconds(inputProps);
  const ctx = { format, locale, durationSec, width, height };
  const html = rewriteAssetUrlsForPreview(builder.build(inputProps, ctx));
  return { html, durationSec };
}

export const TEMPLATE_IDS = Object.keys(HYPERFRAMES_TEMPLATES) as TemplateId[];

export function audienceForIdea(id: string): "b2c" | "b2b" | "affiliate" | "brand" {
  if (id.startsWith("ally_001") || id.startsWith("ally_002") || id.startsWith("ally_003") ||
      id.startsWith("ally_004") || id.startsWith("ally_005") || id.startsWith("ally_006")) {
    return "b2c";
  }
  if (id.startsWith("ally_01")) return "b2b";
  if (id.startsWith("ally_02")) return "affiliate";
  if (id.startsWith("ally_03")) return "brand";
  return "brand";
}

export function audienceLabel(a: ReturnType<typeof audienceForIdea>): string {
  return {
    b2c: "Endkunden",
    b2b: "Händler / Partner",
    affiliate: "Vertriebler",
    brand: "Brand & Vision",
  }[a];
}

export function calcReelDurationFrames(
  scenes: QueueItem["scenes"],
  fps = FPS,
  overlays?: QueueItem["overlays"],
  sfxEvents?: QueueItem["sfxEvents"],
  music?: QueueItem["music"],
  voiceover?: QueueItem["voiceover"],
): number {
  // Mirror @engine/compositions/Reel.calcReelDurationFrames sufficiently for
  // the Composer's "totalFrames" derivation. We delegate to the engine helper.
  // Music / Voiceover werden mit übergeben, damit die Timeline-Länge
  // wächst, sobald der User die Audiospur über das Video-Ende hinaus zieht
  // (analog zum Verhalten klassischer Schnittprogramme).
  const item = { scenes, overlays, sfxEvents, music, voiceover } as QueueItem;
  return Math.max(1, Math.round(engineCalcReelDurationSec(item) * fps));
}
